import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { ShieldCheck } from 'lucide-react';
import { Card } from '../ui/card';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import {
  GET_TINYCMS_SETTINGS,
  UPDATE_TINYCMS_SETTINGS,
  UPDATE_TINYCMS_STYLE_DOCUMENT,
} from '../../graphql';
import { useAccount } from '../../context/AccountContext';
import { Loader } from '../common/PageLoader';
import { useLeaveGuard } from '../../lib/useLeaveGuard';

/**
 * tinycms publishing settings: whether the copy-edit check is required, and the
 * two documents it reads — the style guide and the voice notes.
 *
 * The requirement toggle is admin-only (the server enforces it; canManage just
 * keeps the control honest). The documents are editable by any member, and the
 * agent can edit them too, so the screen always shows what is stored rather
 * than what was last typed here.
 */

type StyleKind = 'style_guide' | 'voice_notes';

interface TinyCmsSettings {
  copyEditRequired: boolean;
  styleGuide: string;
  voiceNotes: string;
  styleGuideUpdatedAt: string | null;
  voiceNotesUpdatedAt: string | null;
  canManage: boolean;
}

const textareaClassName =
  'w-full min-h-48 px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 font-mono text-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';

const formatWhen = (iso: string | null) =>
  iso ? `Last saved ${new Date(iso).toLocaleString()}` : 'Not set yet';

function StyleDocumentEditor({
  kind,
  label,
  help,
  placeholder,
  stored,
  updatedAt,
  card,
}: {
  kind: StyleKind;
  label: string;
  help: string;
  placeholder: string;
  stored: string;
  updatedAt: string | null;
  card?: { badge: string; tone?: 'amber' };
}) {
  const { accountId } = useAccount();
  const [draft, setDraft] = useState(stored);
  const [message, setMessage] = useState('');
  const [tone, setTone] = useState<'ok' | 'error'>('ok');

  // Follow the server when it changes (the agent may have edited it).
  useEffect(() => setDraft(stored), [stored]);

  const [save, { loading }] = useMutation(UPDATE_TINYCMS_STYLE_DOCUMENT, {
    refetchQueries: [{ query: GET_TINYCMS_SETTINGS, variables: { accountId } }],
    onCompleted: () => {
      setTone('ok');
      setMessage('Saved.');
    },
    onError: (err) => {
      setTone('error');
      setMessage(err.message);
    },
  });

  const dirty = draft !== stored;
  useLeaveGuard(dirty, 'Your style guide changes aren’t saved. Leave without saving?');

  if (card) {
    return (
      <div className="ink-pub-card">
        <div className="ink-pub-card-head">
          <span className="ink-pub-title">{label}</span>
          <span className="ink-pub-badge" data-tone={card.tone}>
            {card.badge}
          </span>
        </div>
        <textarea
          id={`tinycms-${kind}`}
          className="ink-pub-doc"
          value={draft}
          aria-label={label}
          onChange={(e) => {
            setDraft(e.target.value);
            setMessage('');
          }}
          placeholder={placeholder}
        />
        {dirty || message ? (
          <div className="ink-set-save" data-error={tone === 'error' ? 'true' : 'false'}>
            <span>{message || 'Unsaved changes'}</span>
            {dirty ? (
              <button type="button" className="ink-set-discard" onClick={() => setDraft(stored)}>
                Discard
              </button>
            ) : null}
            {dirty ? (
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                disabled={loading || !accountId}
                onClick={() => save({ variables: { accountId, kind, body: draft } })}
              >
                {loading ? 'Saving…' : 'Save'}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label htmlFor={`tinycms-${kind}`}>{label}</Label>
      <p className="text-xs text-cmp-ink-2">{help}</p>
      <Textarea
        id={`tinycms-${kind}`}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          setMessage('');
        }}
        placeholder={placeholder}
        className={textareaClassName}
      />
      <div className="flex items-center gap-3">
        <Button
          size="sm"
          disabled={!dirty || loading || !accountId}
          onClick={() => save({ variables: { accountId, kind, body: draft } })}
        >
          {loading ? 'Saving…' : 'Save'}
        </Button>
        {dirty && (
          <Button size="sm" variant="ghost" onClick={() => setDraft(stored)}>
            Discard changes
          </Button>
        )}
        <span className={`text-xs ${tone === 'error' ? 'text-red-400' : 'text-cmp-ink-2'}`}>
          {message || (dirty ? 'Unsaved changes' : formatWhen(updatedAt))}
        </span>
      </div>
    </div>
  );
}

export function PublishingSettings({ embedded = false }: { embedded?: boolean }) {
  const { accountId, isLoading: accountsLoading } = useAccount();
  const [message, setMessage] = useState('');

  const { data, loading } = useQuery<{ tinycmsSettings: TinyCmsSettings }>(GET_TINYCMS_SETTINGS, {
    variables: { accountId },
    skip: !accountId,
  });

  const [updateSettings, { loading: saving }] = useMutation(UPDATE_TINYCMS_SETTINGS, {
    refetchQueries: [{ query: GET_TINYCMS_SETTINGS, variables: { accountId } }],
    onError: (err) => setMessage(err.message),
    onCompleted: () => setMessage(''),
  });

  if (accountsLoading || loading) return <Loader label="Loading" className="ink-set-empty" />;
  const s = data?.tinycmsSettings;
  if (!s) return <p className="ink-set-empty">Publishing settings are unavailable.</p>;

  const toggle = (checked: boolean) =>
    updateSettings({ variables: { accountId, input: { copyEditRequired: checked } } });

  if (embedded) {
    return (
      <div className="ink-pub-stack">
        <button
          type="button"
          className="ink-pub-toggle"
          disabled={!s.canManage || saving}
          onClick={() => toggle(!s.copyEditRequired)}
        >
          <div style={{ flex: 1 }}>
            <div className="ink-pub-title">Require a copy edit before publishing</div>
            <div className="ink-pub-help">
              {s.copyEditRequired
                ? 'Spelling, grammar or style-guide errors block the publish. Structural checks (required fields, alt text, links) always run.'
                : 'The copy edit is optional. What it finds is shown as suggestions and never stops a publish. Structural checks still run.'}
              {s.canManage ? '' : ' Only an account admin can change this.'}
            </div>
            {message ? (
              <div className="ink-pub-help" style={{ color: 'var(--ink-amber)' }}>
                {message}
              </div>
            ) : null}
          </div>
          <span className="ink-switch" data-on={s.copyEditRequired ? 'true' : 'false'} />
        </button>
        <div className="ink-pub-grid">
          <StyleDocumentEditor
            card={{ badge: 'Errors', tone: 'amber' }}
            kind="style_guide"
            label="Style guide"
            help=""
            placeholder={
              '- Write "percent", never %.\n- Use the Oxford comma.\n- Spell out numbers under ten.'
            }
            stored={s.styleGuide}
            updatedAt={s.styleGuideUpdatedAt}
          />
          <StyleDocumentEditor
            card={{ badge: 'Suggestions only' }}
            kind="voice_notes"
            label="Voice"
            help=""
            placeholder="Plain, direct, a little dry. Short sentences. No exclamation marks."
            stored={s.voiceNotes}
            updatedAt={s.voiceNotesUpdatedAt}
          />
        </div>
        <p className="ink-pub-note">
          Your agent reads both, and can update them when you ask. A change means articles need a
          fresh copy edit.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <h2 className="text-lg font-semibold text-cmp-ink flex items-center gap-2">
          <ShieldCheck className="w-5 h-5" /> Publishing
        </h2>
        <p className="text-sm text-cmp-ink-2 mt-1">
          Checks every article goes through before it can be published, and the house rules the copy
          edit uses. Publishing and scheduling always need your approval in chat, whatever these
          settings say.
        </p>
      </div>

      <Card className="p-4 space-y-3 bg-cmp-raise border border-cmp-line text-cmp-ink">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Label htmlFor="copyEditRequired">Require a copy edit before publishing</Label>
            <p className="text-xs text-cmp-ink-2 mt-1">
              {s.copyEditRequired
                ? 'On: an article can only be published after the copy edit has checked that exact version, and spelling, grammar or style-guide errors it finds stop the publish.'
                : 'Off: the copy edit is optional. You can still run it, but what it finds is shown as suggestions and never stops a publish.'}{' '}
              Structural checks (required fields, alt text, links) apply either way.
            </p>
          </div>
          <Switch
            id="copyEditRequired"
            checked={s.copyEditRequired}
            disabled={!s.canManage || saving}
            onCheckedChange={(checked) =>
              updateSettings({ variables: { accountId, input: { copyEditRequired: checked } } })
            }
          />
        </div>
        {!s.canManage && (
          <p className="text-xs text-cmp-ink-2">Only an account admin can change this.</p>
        )}
        {message && <p className="text-xs text-red-400">{message}</p>}
      </Card>

      <Card className="p-4 space-y-6 bg-cmp-raise border border-cmp-line text-cmp-ink">
        <StyleDocumentEditor
          kind="style_guide"
          label="Style guide"
          help="Your house rules: spelling and capitalization, numbers, banned words, punctuation (e.g. the Oxford comma). The copy edit enforces explicit rules here as errors. Changing it means articles need a fresh copy edit."
          placeholder={
            '- Write "percent", never the % sign.\n- Use the Oxford comma.\n- Spell out numbers under ten.'
          }
          stored={s.styleGuide}
          updatedAt={s.styleGuideUpdatedAt}
        />
        <StyleDocumentEditor
          kind="voice_notes"
          label="Voice notes"
          help="How you sound: tone, sentence length, words you like or avoid. The copy edit uses these for suggestions only — voice never blocks a publish."
          placeholder="Plain, direct, a little dry. Short sentences. No exclamation marks."
          stored={s.voiceNotes}
          updatedAt={s.voiceNotesUpdatedAt}
        />
        <p className="text-xs text-cmp-ink-2">
          Your AI agent reads both documents, and can update them when you ask it to — changes made
          either way show up here.
        </p>
      </Card>
    </div>
  );
}
